// AUPRÉA — Étape 15 : détail d'un bilan (admin)
import { useEffect, useMemo, useState } from "react";
import { C, Modal, Spinner, fmtEur } from "./shared";
import { getB, qIndex, optLabel, TYPES, STATUTS } from "./bilansShared";

const Bloc = ({ titre, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{titre}</div>{children}
  </div>
);

const lignesDe = (d, idx) => {
  if (d.reponses_detail.length) return d.reponses_detail.map(r => ({ code: r.code, texte: r.libelle || idx[r.code]?.texte || r.code, section: r.section || idx[r.code]?.section, rep: r.reponse ?? r.valeur, conf: r.confidentiel }));
  const all = { ...d.reponses, ...d.reponses_complementaires };
  Object.values(d.donnees_specifiques || {}).forEach(s => { if (s && typeof s === "object" && !Array.isArray(s)) Object.entries(s).forEach(([k, v]) => { if (idx[k] && !(k in all)) all[k] = v; }); });
  return Object.entries(all).filter(([, v]) => typeof v !== "object").map(([k, v]) => ({ code: k, texte: idx[k]?.texte || k, section: idx[k]?.section, rep: optLabel(idx[k], v), conf: k in d.reponses_complementaires }));
};

export const BilanDetail = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { getB(`/admin/bilans/${id}`).then(setD); }, [id]);
  const idx = useMemo(() => qIndex(d?.type), [d?.type]);
  const fi = d?.fiche || {}, r = d?.resume || {};
  return (
    <Modal wide title={d ? `${TYPES[d.type] || d.type} · ${`${fi.prenom || ""} ${fi.nom || ""}`.trim()}` : "Bilan"} onClose={onClose}>
      {!d ? <Spinner /> : (
        <div data-testid="bilan-detail" style={{ fontSize: 12.5 }}>
          <Bloc titre="Client">
            <div>{fi.email || "—"} · {fi.telephone || "—"}</div>
            <div>{r.age != null ? `${r.age} ans (${r.tranche_age})` : "Âge inconnu"} · {fi.code_postal || "—"} · {r.territoire}</div>
            <div style={{ color: C.muted }}>Consultant : {r.consultant || "Non attribué"} · Statut : {STATUTS[r.statut_dossier] || r.statut_dossier} · RGPD : {fi.rgpd_accept ? `accepté${fi.rgpd_accept_at ? ` le ${new Date(fi.rgpd_accept_at).toLocaleDateString("fr-FR")}` : ""}` : "non renseigné"}</div>
          </Bloc>
          <Bloc titre="Scores">
            <div data-testid="bilan-detail-score">{r.score != null ? `Score global : ${r.score} %` : "Score : —"}{Object.entries(r.sections || {}).map(([k, v]) => ` · ${k} : ${v} %`).join("")}</div>
            {r.verdict && <div>Verdict : {r.verdict}</div>}
            {d.scores?.niveau && <div>Complexité : {d.scores.niveau}</div>}
          </Bloc>
          <Bloc titre="Réponses du client">
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="bilan-detail-reponses"><tbody>
              {lignesDe(d, idx).map(l => (
                <tr key={l.code} style={{ borderTop: `.5px solid ${C.border}` }}>
                  <td style={{ padding: 6, color: C.navy }}>{l.texte}<div style={{ fontSize: 10.5, color: C.muted }}>{l.code}{l.section ? ` · ${l.section}` : ""}{l.conf ? " · confidentielle" : ""}</div></td>
                  <td style={{ padding: 6, fontWeight: 600, width: "35%" }}>{String(l.rep ?? "—")}</td>
                </tr>))}
            </tbody></table>
          </Bloc>
          {d.donnees_specifiques?.societe && <Bloc titre="Entreprise">{Object.entries(d.donnees_specifiques.societe).map(([k, v]) => <div key={k}>{k} : {String(v)}</div>)}</Bloc>}
          <Bloc titre="Préconisations">{d.preconisations.length ? d.preconisations.map((p, i) => <div key={i} style={{ marginBottom: 4 }}>• <strong>{p.titre || p.label || ""}</strong> {p.texte || p.description || ""}</div>) : <span style={{ color: C.muted }}>Aucune</span>}</Bloc>
          <Bloc titre="Proposition liée">
            {d.proposition ? <div data-testid="bilan-detail-proposition">{d.proposition.numero ? `N° ${d.proposition.numero} · ` : ""}{d.proposition.envoyee ? `envoyée le ${new Date(d.proposition.date_publication).toLocaleDateString("fr-FR")}` : (d.proposition.statut || "brouillon")}{d.proposition.total ? ` · ${fmtEur(d.proposition.total)}` : ""}{d.proposition.payment_status === "paid" ? " · payée" : ""}</div> : <span style={{ color: C.muted }}>Aucune proposition</span>}
          </Bloc>
        </div>
      )}
    </Modal>
  );
};
